import { useApolloClient } from '@apollo/client';
import {
  useState,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

export type RoleDefinition =
  | 'Superuser'
  | 'AuthAdmin'
  | 'Devices'
  | 'Zones'
  | 'StudioWeb'
  | 'Installer';

export type UserContextValue = {
  roles: RoleDefinition[];
  clientId: string;
  isSuperUser: boolean;
  sidebarCollapsed?: boolean;
};

const initialValue: UserContextValue = {
  roles: [],
  clientId: '',
  isSuperUser: false,
  sidebarCollapsed: false,
};

/**
 * UserContext is used for app-wide user type values that are either not stored elsewhere or are harder
 * to access. It is primarily updated by useAuth.
 */
export function UserContextProvider({ children }: { children: React.ReactNode }) {
  const [value, setValue] = useState(initialValue);
  const apollo = useApolloClient();
  // Two refs because we have two distinct concerns:
  //   - prevClientIdRef tracks the previous value to detect changes.
  //   - haveSeenRealClientIdRef latches once we've observed a non-empty
  //     clientId so a later clientId='' → real transition (e.g. an org
  //     switcher that briefly clears state) DOES reset the cache, while
  //     the very first '' → real transition on page load does NOT (in-
  //     flight first-paint queries would otherwise be cancelled, giving
  //     a flash-of-loading).
  const prevClientIdRef = useRef<string>('');
  const haveSeenRealClientIdRef = useRef(false);
  useEffect(() => {
    const prev = prevClientIdRef.current;
    prevClientIdRef.current = value.clientId;

    if (!value.clientId) {
      // Empty / cleared. Nothing to reset to. The latch stays as-is, so a
      // subsequent set-back to a real id will be treated as a real change.
      return;
    }
    if (!haveSeenRealClientIdRef.current) {
      // First-ever real clientId. Don't reset — first-paint queries are
      // already in flight and a reset cancels them.
      haveSeenRealClientIdRef.current = true;
      return;
    }
    if (prev !== value.clientId) {
      apollo.resetStore();
    }
  }, [apollo, value.clientId]);
  const contextValue = useMemo(() => ({ ...value, setValue }), [value, setValue]);
  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
}

export const UserContext = createContext<
  UserContextValue & {
    setValue: Dispatch<SetStateAction<UserContextValue>>;
  }
>({ ...initialValue, setValue: () => {} });

export const useUserContext = () => {
  const context = useContext(UserContext);

  return context;
};
